import React from 'react';
import { Card, Space, Button, Typography, Tag, Row, Col, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, MoreOutlined } from '@ant-design/icons';
import { useCategoryRender } from './useCategoryRender';
import { useStatusRender } from './useStatusRender';
import { useUserTimestampRender } from '@src/hooks';

const { Text } = Typography;

interface OfferingsCardViewProps {
  dataSource: any[];
  onEditClick?: (record: any) => void;
  onDeleteClick?: (record: any) => void;
  onViewClick?: (record: any) => void;
  onRowClick?: (record: any) => void;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;
  customActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (record: any) => void;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    tooltip?: string;
    confirmMessage?: string;
  }>;
  rowKey?: string;
}

export const useOfferingsCardView = () => {
  const getCategoryConfig = useCategoryRender();
  const getStatusConfig = useStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  const OfferingsCardView: React.FC<OfferingsCardViewProps> = ({
    dataSource,
    onEditClick,
    onDeleteClick,
    onViewClick,
    onRowClick,
    showEdit = true,
    showDelete = true,
    showView = false,
    customActions = [],
    rowKey = 'id',
  }) => {

    // Helper function to render actions
    const renderActions = (record: any) => {
      const actions = [];

      if (showView) {
        actions.push(
          <Button
            key="view"
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onViewClick?.(record);
            }}
            style={{ color: '#1890ff' }}
          />
        );
      }

      if (showEdit) {
        actions.push(
          <Button
            key="edit"
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick?.(record);
            }}
            style={{ color: '#faad14' }}
          />
        );
      }

      if (showDelete) {
        actions.push(
          <Button
            key="delete"
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick?.(record);
            }}
            style={{ color: '#ff4d4f' }}
          />
        );
      }

      // Add custom actions
      customActions.forEach((action) => {
        actions.push(
          <Button
            key={action.key}
            type="text"
            size="small"
            icon={action.icon}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(record);
            }}
            style={{ 
              color: action.danger ? '#ff4d4f' : action.type === 'primary' ? '#1890ff' : '#666'
            }}
          />
        );
      });

      return actions;
    };

    return (
      <div className="p-0">
        {dataSource.map((record, index) => {
          const name = record.name || 'Unknown Offering';
          const category = record.category || '';
          const status = record.status || false;
          const price = record.price || 0;
          const discount = record.discount || 0;
          const duration = record.duration_in_mins || 0;
          const sessions = record.total_sessions || 0;
          const createdAt = record.created_at || '';

          const categoryConfig = getCategoryConfig(category);
          const statusConfig = getStatusConfig(status);

          return (
            <Card
              key={record[rowKey] || index}
              size="small"
              className={`
                mb-4 mx-4 
                ${index === 0 ? 'mt-4' : 'mt-0'}
                rounded-xl shadow-sm border border-gray-200 
                ${onRowClick ? 'cursor-pointer' : 'cursor-default'}
                transition-all duration-200 ease-in-out bg-white
              `}
              bodyStyle={{ padding: '16px' }}
              onClick={() => onRowClick?.(record)}
            >
              {/* Top Section - ID, Name, Category, Status */}
              <Row gutter={[12, 8]} align="middle" className="mb-3">
                <Col flex="auto">
                  <Space size="small">
                    <Text className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      #{record[rowKey]}
                    </Text>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="text-sm font-bold text-black leading-tight mb-1">
                          {name}
                        </div>
                      </div>
                       <Space size="small">
                         {category && (
                           <Tag 
                             color={categoryConfig.color}
                             style={{ 
                               fontSize: '10px',
                               padding: '2px 6px',
                               height: 'auto',
                               lineHeight: '14px'
                             }}
                           >
                             {categoryConfig.icon} {categoryConfig.text}
                           </Tag>
                         )}
                       </Space>
                    </div>
                  </Space>
                </Col>
                <Col>
                   <Space size="small">
                     <Tag
                       color={statusConfig.textColor}
                       style={{
                         backgroundColor: statusConfig.bgColor,
                         borderColor: statusConfig.borderColor,
                         color: statusConfig.textColor,
                         fontWeight: 500,
                         fontSize: '10px',
                         padding: '2px 6px',
                         height: 'auto',
                         lineHeight: '14px'
                       }}
                     >
                       {statusConfig.icon} {statusConfig.text}
                     </Tag>
                   </Space>
                </Col>
              </Row>

              {/* Content Section - Price, Duration, Discount, Sessions */}
              <Row gutter={[16, 8]} className="mb-3">
                <Col span={12}>
                  <div>
                    <Text type="secondary" className="text-xs">
                      Price
                    </Text>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Text className="text-sm font-medium">
                        ₹{price}
                      </Text>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Text type="secondary" className="text-xs">
                      Duration
                    </Text>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Text className="text-sm font-medium">
                        {duration ? `${duration} mins` : '-'}
                      </Text>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row gutter={[16, 8]} className="mb-3">
                <Col span={12}>
                  <div>
                    <Text type="secondary" className="text-xs">
                      Discount
                    </Text>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Text className="text-sm font-medium">
                        {discount > 0 ? `${discount}%` : '-'}
                      </Text>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Text type="secondary" className="text-xs">
                      Sessions
                    </Text>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Text className="text-sm font-medium">
                        {sessions ? `${sessions}` : '-'}
                      </Text>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Bottom Section - Last Updated and Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <div className="flex items-center gap-2">
                  {userTimestampRender(record)}
                  {renderActions(record)}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  return { OfferingsCardView };
};
